import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthCard from "@/components/auth/AuthCard";
import AuthButton from "@/components/auth/AuthButton";
import AuthLink from "@/components/auth/AuthLink";
import { useAuth, formatAuthError } from "@/context/AuthContext";

// VerifyEmail handles TWO cases:
// 1. Token present in URL (`?token=xxx` or `?token=xxx&kind=magic`) —
//    auto-consume, then redirect to /collections.
// 2. No token — the post-signup "check your inbox" screen with resend.
export default function VerifyEmail() {
  const { verifyEmail, consumeMagicLink, magicLink } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const token = params.get("token");
  const kind = params.get("kind") || "verify";
  const emailFromState = location.state?.email || "";

  const [status, setStatus] = useState(token ? "consuming" : "idle");
  const [error, setError] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const [resentOk, setResentOk] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        if (kind === "magic") {
          await consumeMagicLink(token);
          setStatus("done");
          return;
        }
        // Plain email verification: no auto-login. Redirect straight to
        // login with a success flag so the login page can surface a banner.
        const r = await verifyEmail(token);
        setStatus("done");
        setSuccessMessage(r?.message || "Email verified. You may now log in.");
        return;
      } catch (err) {
        setStatus("error");
        setError(formatAuthError(err, "This link is invalid or has expired."));
      }
    })();
  }, [token, kind, consumeMagicLink, verifyEmail, navigate]);

  const resend = async () => {
    if (!emailFromState) { setError("No email to resend to — sign up again."); return; }
    setResendBusy(true); setError("");
    try {
      // The signup path already sent a verify email. For resend we re-use
      // the magic-link endpoint so the user can complete verification via
      // a fresh single-use link.
      await magicLink(emailFromState);
      setResentOk(true);
    } catch (err) { setError(formatAuthError(err)); }
    finally { setResendBusy(false); }
  };

  if (token) {
    // Plain verify-email flow — success now shows "Go to Login" (no
    // auto-redirect, no auto-login). Magic-link flow still auto-navigates
    // via the useEffect above.
    if (status === "done" && kind !== "magic") {
      return (
        <AuthCard title="Email verified" subtitle={successMessage}>
          <AuthButton
            variant="charcoal"
            onClick={() => navigate("/auth/login", { replace: true })}
            testid="verify-goto-login"
          >
            Go to Login
          </AuthButton>
        </AuthCard>
      );
    }
    return (
      <AuthCard
        title={status === "error" ? "Link invalid" : "Verifying your email…"}
        subtitle={status === "error" ? error : "One moment while we sign you in."}
        footer={status === "error" ? <AuthLink to="/auth/login" testid="verify-error-back">Back to login</AuthLink> : null}
      >
        {status === "consuming" && (
          <p className="text-xs text-center" style={{ color: "#9a9a9a" }} data-testid="verify-consuming">Please wait…</p>
        )}
        {status === "done" && (
          <p className="text-xs text-center" style={{ color: "#a8dca8" }} data-testid="verify-done">Verified. Redirecting…</p>
        )}
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Verify your email"
      subtitle={emailFromState ? `We sent a verification link to ${emailFromState}` : "We sent a verification link to your email"}
      footer={<AuthLink to="/auth/login" testid="verify-back-login">Back to login</AuthLink>}
    >
      <p className="text-xs text-center mb-6" style={{ color: "#9a9a9a" }}>
        The link expires in 10 minutes and can only be used once.
      </p>
      <AuthButton variant="charcoal" onClick={resend} loading={resendBusy} testid="verify-resend">
        Resend link
      </AuthButton>
      {resentOk && (
        <p className="text-xs text-center mt-4" style={{ color: "#a8dca8" }} data-testid="verify-resent">
          Sent — check your inbox.
        </p>
      )}
      {error && (
        <p className="text-xs text-center mt-4" style={{ color: "#c86a6a" }} data-testid="verify-error">
          {error}
        </p>
      )}
    </AuthCard>
  );
}
